<script setup lang="ts">
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
  type ActiveElement,
  type ChartData,
  type ChartEvent,
  type ChartOptions,
} from 'chart.js'
import { computed } from 'vue'
import { Bar } from 'vue-chartjs'
import {
  baseBarchartDatasetSettings,
  getCSSVariableColor,
  normalizeDataset,
  retrieveClickedChartDatum,
  tooltipStatsLabelFor,
  type BarchartDataset,
  type ChartClickEventDatum,
} from '@/components/rc/work-order-dashboard/charts-helpers'
import type { MinerServicesStatistics } from '@/types/rc/work-orders'
import { useDark } from '@vueuse/core'

const props = defineProps<{ totalMiners: number; stats: MinerServicesStatistics }>()
const emit = defineEmits<{ (event: 'barClick', value: ChartClickEventDatum): void }>()

const isDark = useDark()

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend)

// These should be derived from props
const labels = Object.keys(props.stats)

const sourceData = computed<[number[], number[]]>(() => [
  Object.values(props.stats),
  Object.values(props.stats).map((n) => props.totalMiners - n),
])

const servicesBarchartSettings = [
  { backgroundColor: getCSSVariableColor('--color-sprout-400'), label: 'Performed' },
  {
    backgroundColor: isDark
      ? getComputedStyle(document.documentElement).getPropertyValue('--component-fill-badge')
      : '#e2e2e2',
    label: 'Not performed',
  },
]

const baseData = computed<BarchartDataset[]>(() =>
  sourceData.value.map((data, i) => ({
    ...servicesBarchartSettings[i],
    ...baseBarchartDatasetSettings.value,
    barThickness: 48 - 2 * labels.length,
    data,
  }))
)

const chartData = computed<ChartData<'bar', number[], string>>(() => ({
  labels,
  datasets: normalizeDataset(baseData.value),
}))

// Emit 'barClick' so parent can filter out the table
const onClick = (event: ChartEvent, elements: ActiveElement[]) =>
  emit('barClick', retrieveClickedChartDatum(chartData, elements))

const chartOptions = computed<ChartOptions<'bar'>>(() => ({
  responsive: true,
  indexAxis: 'y',
  onClick,
  scales: {
    x: { stacked: true, grid: { display: false }, min: 0, max: 100, display: false },
    y: {
      stacked: true,
      grid: { display: false },
      ticks: { align: 'start', crossAlign: 'far' },
      border: { display: false },
    },
  },
  plugins: {
    tooltip: {
      enabled: true,
      callbacks: {
        label: tooltipStatsLabelFor(baseData),
      },
    },
    legend: {
      position: 'bottom',
      labels: {
        usePointStyle: true,
      },
    },
  },
}))
</script>

<template>
  <div class="w-full h-[400px] flex items-center justify-center">
    <Bar :data="chartData" :options="chartOptions" />
  </div>
</template>
